import { useCallback, useEffect, useState } from "react";
import { Gavel, FileSearch, Lock, Loader2, Check, X, Stamp, Car } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DEMO } from "@/components/cef/cefShared";

/* The bench's warrant desk — judges endorse active arrest warrants (units get
   texted "serve on sight") and rule on phone-record subpoena petitions from Sgt+.
   In demo the actions play out client-side. */

const SUB_STATUS = { pending: "text-nc-orange border-nc-orange/50", approved: "text-nc-green border-nc-green/50", denied: "text-red-400 border-red-500/50" };

export default function CourtDesk({ serverId, token, demo }) {
  const [desk, setDesk] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [judgeName, setJudgeName] = useState("");
  const [licPc, setLicPc] = useState("");
  const [lic, setLic] = useState(null); // {playercode, points, suspended, threshold, last_test} | {notfound:true}

  const load = useCallback(() => {
    if (demo) { setDesk(DEMO.courtDesk); setErr(""); return; }
    api.get(`/nc/servers/${serverId}/court/desk`, { params: { token } })
      .then((r) => { setDesk(r.data); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "Warrant desk offline"));
  }, [demo, serverId, token]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const sign = async (w) => {
    if (demo) {
      setDesk((d) => ({ ...d, warrants: d.warrants.map((x) => x.id === w.id ? { ...x, signed_by: judgeName || "R. Castellanos" } : x) }));
      toast.success(`Warrant on ${w.name} signed — units notified`);
      return;
    }
    setBusy(w.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/court/desk/warrants/${w.id}/sign`, { judge_name: judgeName }, { params: { token } });
      toast.success(r.data.message); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const rule = async (s, grant) => {
    if (demo) {
      setDesk((d) => ({ ...d, subpoenas: d.subpoenas.map((x) => x.id === s.id ? { ...x, status: grant ? "approved" : "denied", decided_by: `Judge ${judgeName || "R. Castellanos"}` } : x) }));
      toast.success(`Subpoena ${grant ? "granted" : "denied"}`);
      return;
    }
    setBusy(s.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/court/desk/subpoenas/${s.id}/decide`, { grant, judge_name: judgeName }, { params: { token } });
      toast.success(r.data.message); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const checkLicence = async () => {
    if (!/^\d+$/.test(licPc.trim())) return;
    if (demo) { setLic({ playercode: +licPc, points: 14, suspended: true, threshold: 12, last_test: { passed: false, examiner: "DMV Kiosk 3", at: new Date().toISOString() } }); return; }
    setBusy("lic");
    try { const r = await api.get(`/nc/servers/${serverId}/cad/licence/${licPc}`, { params: { token } }); setLic(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLic(null); }
    finally { setBusy(""); }
  };

  const reinstate = async () => {
    if (demo) { setLic((l) => ({ ...l, points: 0, suspended: false })); toast.success("Licence reinstated — points cleared"); return; }
    setBusy("lic");
    try {
      const r = await api.post(`/nc/servers/${serverId}/court/desk/licence/${lic.playercode}/reinstate`, { judge_name: judgeName }, { params: { token } });
      toast.success(r.data.message);
      setLic((l) => ({ ...l, points: 0, suspended: false }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (err) return <div className="flex-1 flex items-center justify-center p-10"><p data-testid="desk-error" className="font-mono text-[11px] text-red-400">{err}</p></div>;
  if (!desk) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-nc-orange" /></div>;

  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="court-desk">
      <div className="max-w-3xl flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Bench signature block — everything here awaits a judge</p>
          <input data-testid="desk-judge-name" value={judgeName} onChange={(e) => setJudgeName(e.target.value)} placeholder="Your honour's name"
            className="ml-auto bg-black border border-white/15 px-3 py-2 font-mono text-[10px] w-56 focus:outline-none focus:ring-1 focus:ring-nc-orange" />
        </div>

        {/* warrants */}
        <div>
          <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><Gavel className="w-3 h-3 text-nc-orange" /> Active arrest warrants ({desk.warrants.length})</p>
          {desk.warrants.map((w) => (
            <div key={w.id} data-testid={`desk-warrant-${w.id.slice(0, 6)}`} className="border border-white/10 bg-black/30 p-4 mb-2 flex flex-col gap-2">
              <div className="flex items-center gap-2.5 flex-wrap">
                <p className="font-sans font-semibold text-sm">{w.name} <span className="font-mono text-[9px] text-zinc-500">#{w.playercode}</span></p>
                {w.serious && <span className="font-mono text-[8px] uppercase text-red-400 border border-red-500/50 px-1.5 py-0.5">Serious</span>}
                {w.funds_locked && <span className="font-mono text-[8px] uppercase text-nc-orange border border-nc-orange/50 px-1.5 py-0.5 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Funds locked</span>}
                {w.signed_by
                  ? <span data-testid={`desk-signed-${w.id.slice(0, 6)}`} className="ml-auto font-mono text-[8px] uppercase text-nc-green border border-nc-green/50 px-2 py-1 flex items-center gap-1"><Stamp className="w-3 h-3" /> Signed · Judge {w.signed_by}</span>
                  : <button data-testid={`desk-sign-${w.id.slice(0, 6)}`} disabled={busy === w.id} onClick={() => sign(w)}
                      className="ml-auto font-mono text-[8px] uppercase border border-nc-orange/60 text-nc-orange px-3 py-1.5 hover:bg-nc-orange/10 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                      {busy === w.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stamp className="w-3 h-3" />} Sign warrant
                    </button>}
              </div>
              <p className="font-mono text-[9.5px] text-zinc-400 leading-snug">{w.reason}</p>
              <p className="font-mono text-[8px] text-zinc-600 uppercase">sworn by {w.issued_by} · {String(w.at).slice(0, 16).replace("T", " ")}</p>
            </div>
          ))}
          {desk.warrants.length === 0 && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em] py-3">No active warrants before the bench.</p>}
        </div>

        {/* subpoenas */}
        <div>
          <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><FileSearch className="w-3 h-3 text-nc-orange" /> Phone-record subpoena petitions ({desk.subpoenas.length})</p>
          {desk.subpoenas.map((s) => (
            <div key={s.id} data-testid={`desk-subpoena-${s.id.slice(0, 6)}`} className="border border-white/10 bg-black/30 p-4 mb-2 flex flex-col gap-2">
              <div className="flex items-center gap-2.5 flex-wrap">
                <p className="font-sans font-semibold text-sm">{s.target_name} <span className="font-mono text-[9px] text-zinc-500">#{s.target_playercode}</span></p>
                <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${SUB_STATUS[s.status]}`}>{s.status}</span>
                <span className="ml-auto font-mono text-[8px] text-zinc-600 uppercase">petitioned by {s.requester_name}{s.decided_by ? ` · ${s.decided_by}` : ""}</span>
              </div>
              <p className="font-mono text-[9.5px] text-zinc-400 leading-snug">{s.reason}</p>
              {s.status === "pending" && (
                <div className="flex gap-2 mt-1">
                  <button data-testid={`desk-grant-${s.id.slice(0, 6)}`} disabled={busy === s.id} onClick={() => rule(s, true)}
                    className="font-mono text-[8px] uppercase border border-nc-green/60 text-nc-green px-3 py-1.5 hover:bg-nc-green/10 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    {busy === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Grant — unseal records
                  </button>
                  <button data-testid={`desk-deny-${s.id.slice(0, 6)}`} disabled={busy === s.id} onClick={() => rule(s, false)}
                    className="font-mono text-[8px] uppercase border border-red-500/50 text-red-400 px-3 py-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    <X className="w-3 h-3" /> Deny
                  </button>
                </div>
              )}
            </div>
          ))}
          {desk.subpoenas.length === 0 && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em] py-3">No subpoena petitions on file.</p>}
        </div>

        {/* licence reinstatement */}
        <div>
          <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><Car className="w-3 h-3 text-nc-orange" /> Licence reinstatement</p>
          <div className="border border-white/10 bg-black/30 p-4 flex flex-col gap-2">
            <p className="font-mono text-[9px] text-zinc-500 leading-snug">Suspended drivers complete the in-game DMV re-test; the bridge files the result here. A pass — or your court order — clears points and lifts the suspension.</p>
            <div className="flex gap-2">
              <input data-testid="licence-pc" value={licPc} onChange={(e) => setLicPc(e.target.value.replace(/\D/g, ""))} placeholder="Citizen playercode"
                className="bg-black border border-white/15 px-3 py-2 font-mono text-[10px] w-44 focus:outline-none focus:ring-1 focus:ring-nc-orange" />
              <button data-testid="licence-check" onClick={checkLicence} disabled={busy === "lic" || !licPc.trim()}
                className="font-mono text-[8px] uppercase border border-white/15 text-zinc-300 px-3 py-2 hover:border-nc-orange hover:text-nc-orange transition-colors disabled:opacity-40 flex items-center gap-1.5">
                {busy === "lic" ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Check licence
              </button>
            </div>
            {lic && (
              <div data-testid="licence-card" className="border border-white/10 p-3 flex flex-col gap-1.5">
                <p className="font-mono text-[10px] text-zinc-200">
                  #{lic.playercode} · <span className={lic.suspended ? "text-red-400 font-bold" : "text-nc-green font-bold"}>{lic.suspended ? `SUSPENDED — ${lic.points}/${lic.threshold} pts` : `VALID — ${lic.points}/${lic.threshold} pts`}</span>
                </p>
                {lic.last_test && (
                  <p className="font-mono text-[8.5px] text-zinc-500 uppercase">Last re-test: {lic.last_test.passed ? "passed" : "failed"} · {lic.last_test.examiner} · {String(lic.last_test.at).slice(0, 10)}</p>
                )}
                {lic.suspended && (
                  <button data-testid="licence-reinstate" onClick={reinstate} disabled={busy === "lic"}
                    className="w-fit font-mono text-[8px] uppercase border border-nc-green/60 text-nc-green px-3 py-1.5 hover:bg-nc-green/10 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    {busy === "lic" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Reinstate by court order
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
